/**
 * AI Support - Main Dashboard Component
 *
 * Displays:
 * - AI agent overview card
 * - Recent conversations list
 * - Analytics dashboard
 * - Conversation detail view
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { Bot, Phone, Settings, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AIAgent, AIConversationSummary } from '@/types/ai-support';
import { AIConversationList } from './AIConversationList';
import { AIConversationDetail } from './AIConversationDetail';
import { AIAgentConfig } from './AIAgentConfig';
import { AIAnalytics } from './AIAnalytics';

const log = createComponentLogger('AISupport', 'ai-support');

export const AISupport: React.FC = () => {

  const [agent, setAgent] = useState<AIAgent | null>(null);
  const [conversations, setConversations] = useState<AIConversationSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load AI agent configuration
  useEffect(() => {
    loadAgent();
  }, []);

  // Load conversations when agent is loaded
  useEffect(() => {
    if (agent) {
      loadConversations();
      subscribeToConversations();
    }
  }, [agent]);

  const loadAgent = async () => {
    try {
      setLoading(true);

      // Get the organization's AI agent
      const { data, error: agentError } = await supabase
        .from('ai_voice_agents')
        .select('*')
        .eq('is_active', true)
        .single();

      if (agentError) {
        if (agentError.code === 'PGRST116') {
          // No agent found - this is okay, show setup prompt
          setAgent(null);
        } else {
          throw agentError;
        }
      } else {
        setAgent(data);
      }
    } catch (err) {
      log.error('Error loading AI agent', {}, err as Error);
      setError('Failed to load AI agent configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    if (!agent) {
return;
}

    try {
      const { data, error: convError } = await supabase
        .from('ai_conversation_summaries')
        .select('*')
        .eq('agent_id', agent.id)
        .order('call_started_at', { ascending: false })
        .limit(50);

      if (convError) {
throw convError;
}

      setConversations(data || []);
    } catch (err) {
      log.error('Error loading conversations', { agentId: agent?.id }, err as Error);
    }
  };

  const subscribeToConversations = () => {
    if (!agent) {
return;
}

    // Subscribe to real-time updates
    const channel = supabase
      .channel('ai_conversations_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_conversations',
          filter: `agent_id=eq.${agent.id}`
        },
        () => {
          // Reload conversations when changes detected
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleConfigSaved = () => {
    setShowConfig(false);
    loadAgent();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Bot className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Loading AI Support...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Show setup prompt if no agent configured
  if (!agent && !showConfig) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Bot className="h-8 w-8 text-primary" />
              </div>
              <div>
                <CardTitle>AI Support Agent</CardTitle>
                <CardDescription>
                  24/7 automated phone support powered by OpenAI
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-3">What is AI Support?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Answer borrower questions instantly, 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Lookup loan status and borrower information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Schedule callbacks and create leads automatically</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Full conversation transcripts and analytics</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Natural-sounding voice powered by ElevenLabs</span>
                </li>
              </ul>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Estimated Cost:</strong> ~$0.30/minute of call time
                <br />
                Based on 100 calls/day @ 3 minutes average: ~$2,700/month
              </p>
            </div>

            <Button onClick={() => setShowConfig(true)} className="w-full" size="lg">
              <Settings className="h-4 w-4 mr-2" />
              Configure AI Agent
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show config modal
  if (showConfig) {
    return (
      <AIAgentConfig
        agent={agent}
        onSave={handleConfigSaved}
        onCancel={() => setShowConfig(false)}
      />
    );
  }

  // Calculate stats
  const totalCalls = conversations.length;
  const completedCalls = conversations.filter(c => c.callStatus === 'completed').length;
  const avgDuration = conversations.reduce((sum, c) => sum + (c.callDurationSeconds || 0), 0) / (completedCalls || 1);
  const requiresFollowup = conversations.filter(c => c.requiresFollowup && !c.followupCompletedAt).length;

  // Main dashboard view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Bot className="h-8 w-8" />
            AI Support Agent
          </h1>
          <p className="text-muted-foreground mt-1">
            Powered by OpenAI Realtime API & ElevenLabs
          </p>
        </div>
        <Button onClick={() => setShowConfig(true)} variant="outline">
          <Settings className="h-4 w-4 mr-2" />
          Configure
        </Button>
      </div>

      {/* Agent Status Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-primary/10 rounded-lg">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{agent?.name}</h3>
                <p className="text-sm text-muted-foreground">{agent?.twilioPhoneNumber}</p>
              </div>
            </div>
            <Badge variant={agent?.isActive ? "default" : "secondary"} className="text-sm">
              {agent?.isActive ? "🟢 Active" : "🔴 Inactive"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Calls</CardDescription>
            <CardTitle className="text-3xl">{totalCalls}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Completed Today</CardDescription>
            <CardTitle className="text-3xl">{completedCalls}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Avg Duration</CardDescription>
            <CardTitle className="text-3xl">{Math.floor(avgDuration / 60)}m {Math.floor(avgDuration % 60)}s</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Requires Follow-up</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{requiresFollowup}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="lg:col-span-1">
          <AIConversationList
            conversations={conversations}
            selectedId={selectedConversationId}
            onSelectConversation={setSelectedConversationId}
          />
        </div>

        {/* Conversation Detail or Analytics */}
        <div className="lg:col-span-2">
          {selectedConversationId ? (
            <AIConversationDetail
              conversationId={selectedConversationId}
              onClose={() => setSelectedConversationId(null)}
            />
          ) : (
            <AIAnalytics agentId={agent?.id || ''} />
          )}
        </div>
      </div>
    </div>
  );
};
